import type { AssignmentStatement, Expression, ForStatement, IfStatement, RepeatUntilStatement, Statement, WhileStatement } from '../parser/ast.js';
import { asLinearShift, assignedNames, isDirectNegation, negateCondition } from './selection.js';

/**
 * Μετατροπές δομών επανάληψης (βλ. σχολικό ΑΕΠΠ): ΓΙΑ (μετρητής) ↔ ΟΣΟ
 * (έλεγχος πριν) ↔ ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ (έλεγχος μετά). Ίδια φιλοσοφία με το
 * `selection.ts`: κάθε μετατροπή είτε είναι ΠΑΝΤΑ σωστή είτε ελέγχει ρητά τις
 * προϋποθέσεις της και επιστρέφει `null`/άδειο αποτέλεσμα αντί να παράγει
 * κάτι που θα άλλαζε συμπεριφορά.
 *
 * Οι δύο κρίσιμες παγίδες (και οι δύο διορθωμένες εδώ εξαρχής, με βάση
 * αντίστοιχα λάθη που ήδη βρέθηκαν στις μετατροπές επιλογής):
 * - ΓΙΑ μπορεί να τρέξει 0 φορές (π.χ. `Για i από 5 μέχρι 1`)· το
 *   ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ τρέχει ΠΑΝΤΑ ≥1 φορά. Η μετατροπή ΓΙΑ→ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ
 *   προσφέρεται ΜΟΝΟ όταν τα όρια είναι σταθερές και αποδεδειγμένα ≥1 φορά.
 * - Το ΟΣΟ (0+ φορές) και το ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ (1+ φορές) δεν αντιστοιχίζονται
 *   απευθείας το ένα στο άλλο· χρειάζεται ένας εξωτερικός έλεγχος ΑΝ γύρω
 *   από το ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ ώστε να παραμείνει σωστό όταν η συνθήκη είναι
 *   ψευδής από την αρχή.
 */

/** Η αριθμητική τιμή ενός NumberLiteral, ή ενός NumberLiteral με μπροστά ένα μοναδιαίο πλην (π.χ. "-1" αναλύεται ως UnaryExpression) — αλλιώς `null`. */
export function numericConstant(expr: Expression): number | null {
  if (expr.kind === 'NumberLiteral') return expr.value;
  if (expr.kind === 'UnaryExpression' && expr.operator === '-' && expr.operand.kind === 'NumberLiteral') return -expr.operand.value;
  return null;
}

function incrementAssignment(variable: ForStatement['variable'], step: number, pos: ForStatement): AssignmentStatement {
  const amount: Expression = { kind: 'NumberLiteral', value: Math.abs(step), line: pos.line, column: pos.column };
  const value: Expression = { kind: 'BinaryExpression', operator: step > 0 ? '+' : '-', left: variable, right: amount, line: pos.line, column: pos.column };
  return { kind: 'AssignmentStatement', target: variable, value, line: pos.line, column: pos.column };
}

// --- ΓΙΑ ↔ ΟΣΟ ----------------------------------------------------------------

/**
 * ΓΙΑ (μετρητής) → [ανάθεση αρχικοποίησης, ΟΣΟ]. Πάντα δυνατή ΟΤΑΝ το βήμα
 * είναι γνωστή σταθερά (σιωπηρό +1, ή αριθμητική/μοναδιαίο-πλην σταθερά) —
 * αλλιώς δεν ξέρουμε τη φορά της σύγκρισης (`<=` ή `>=`) και αρνούμαστε.
 */
export function forToWhile(stmt: ForStatement): readonly Statement[] | null {
  const step = stmt.step === null ? 1 : numericConstant(stmt.step);
  if (step === null || step === 0) return null;

  const init: AssignmentStatement = { kind: 'AssignmentStatement', target: stmt.variable, value: stmt.from, line: stmt.line, column: stmt.column };
  const condition: Expression = {
    kind: 'BinaryExpression',
    operator: step > 0 ? '<=' : '>=',
    left: stmt.variable,
    right: stmt.to,
    line: stmt.line,
    column: stmt.column,
  };
  const increment = incrementAssignment(stmt.variable, step, stmt);
  // Συνθετικός κόμβος (μόνο για εμφάνιση κειμένου στο "Μετατροπή δομών", ΔΕΝ
  // εκτελείται/σχεδιάζεται ποτέ ζωντανά) — βλ. σχόλιο `WhileStatement.endLine`.
  const whileStmt: WhileStatement = { kind: 'WhileStatement', condition, body: [...stmt.body, increment], endLine: null, line: stmt.line, column: stmt.column };
  return [init, whileStmt];
}

export interface WhileToForMatch {
  readonly initAssignment: AssignmentStatement;
  readonly whileStatement: WhileStatement;
  readonly apply: () => ForStatement;
}

/**
 * Αναγνωρίζει ΑΚΡΙΒΩΣ το μοτίβο "i ← α" ακολουθούμενο ΑΜΕΣΩΣ από "Όσο i
 * <=|>= β Επανάλαβε ... i ← i ± σταθερά" (η τελευταία εντολή του σώματος,
 * ΚΑΙ καμία άλλη εντολή του σώματος να μην ξαναγράφει το i) — δηλαδή το
 * ακριβές αντίστροφο του `forToWhile`. Exported ΚΑΙ επειδή το
 * `array-fill-suggestion.ts` το χρησιμοποιεί για να αναγνωρίζει "χειροκίνητο
 * ΓΙΑ γραμμένο με ΟΣΟ" σαν έγκυρο περιβάλλον ΓΙΑ για γέμισμα πίνακα.
 */
export function matchWhileAsFor(initStmt: Statement, whileStmt: Statement): WhileToForMatch | null {
  if (initStmt.kind !== 'AssignmentStatement' || initStmt.target.kind !== 'Identifier') return null;
  if (whileStmt.kind !== 'WhileStatement') return null;
  const varName = initStmt.target.name;

  const condition = whileStmt.condition;
  if (condition.kind !== 'BinaryExpression') return null;
  if (condition.operator !== '<=' && condition.operator !== '>=') return null;
  if (condition.left.kind !== 'Identifier' || condition.left.name !== varName) return null;

  if (whileStmt.body.length === 0) return null;
  const lastIndex = whileStmt.body.length - 1;
  const last = whileStmt.body[lastIndex]!;
  if (last.kind !== 'AssignmentStatement' || last.target.kind !== 'Identifier' || last.target.name !== varName) return null;

  const delta = asLinearShift(last.value, varName);
  if (delta === null || delta === 0) return null;
  if (condition.operator !== (delta > 0 ? '<=' : '>=')) return null;

  const bodyWithoutIncrement = whileStmt.body.slice(0, lastIndex);
  if (assignedNames(bodyWithoutIncrement).has(varName)) return null;

  const forStmt: ForStatement = {
    kind: 'ForStatement',
    variable: initStmt.target,
    from: initStmt.value,
    to: condition.right,
    step: delta === 1 ? null : { kind: 'NumberLiteral', value: delta, line: whileStmt.line, column: whileStmt.column },
    body: bodyWithoutIncrement,
    // Συνθετικός κόμβος — δεν εκτελείται/σχεδιάζεται ζωντανά (μόνο εμφάνιση
    // κειμένου στο "Μετατροπή δομών" ή στατικός υπολογισμός στο
    // array-fill-suggestion.ts) — βλ. σχόλιο `ForStatement.endLine`.
    endLine: null,
    line: initStmt.line,
    column: initStmt.column,
  };
  return { initAssignment: initStmt, whileStatement: whileStmt, apply: () => forStmt };
}

/** Σαρώνει μια λίστα εντολών για ΓΕΙΤΟΝΙΚΑ ζεύγη [ανάθεση αρχικοποίησης, ΟΣΟ] που ταιριάζουν στο μοτίβο μετρητή (βλ. `matchWhileAsFor`). */
export function findForLoopsFromWhile(statements: readonly Statement[]): WhileToForMatch[] {
  const matches: WhileToForMatch[] = [];
  for (let i = 0; i + 1 < statements.length; i++) {
    const match = matchWhileAsFor(statements[i]!, statements[i + 1]!);
    if (match !== null) matches.push(match);
  }
  return matches;
}

// --- ΓΙΑ ↔ ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ -----------------------------------------------------

/** Χτίζει [ανάθεση αρχικοποίησης, ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ] από ένα ΓΙΑ με ΓΝΩΣΤΟ (σταθερό) βήμα — ΧΩΡΙΣ να ελέγχει αν αποδεικνύεται ≥1 φορά (αυτό το αποφασίζει ο καλών: `forToRepeatUntil` το απαιτεί άμεσα/στατικά, `forToGuardedRepeatUntil` το ελέγχει στο runtime μέσω ΑΝ). */
function buildRepeatUntilFromFor(stmt: ForStatement, step: number): readonly [AssignmentStatement, RepeatUntilStatement] {
  const init: AssignmentStatement = { kind: 'AssignmentStatement', target: stmt.variable, value: stmt.from, line: stmt.line, column: stmt.column };
  const increment = incrementAssignment(stmt.variable, step, stmt);
  const stopCondition: Expression = {
    kind: 'BinaryExpression',
    operator: step > 0 ? '>' : '<',
    left: stmt.variable,
    right: stmt.to,
    line: stmt.line,
    column: stmt.column,
  };
  const repeatStmt: RepeatUntilStatement = {
    kind: 'RepeatUntilStatement',
    body: [...stmt.body, increment],
    condition: stopCondition,
    line: stmt.line,
    column: stmt.column,
  };
  return [init, repeatStmt];
}

/**
 * ΓΙΑ (μετρητής) → [ανάθεση αρχικοποίησης, ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ]. Το
 * ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ εκτελείται ΠΑΝΤΑ τουλάχιστον 1 φορά, ενώ το ΓΙΑ μπορεί να
 * εκτελεστεί 0 φορές — άρα προσφέρεται ΜΟΝΟ όταν τα `από`/`μέχρι`/`βήμα`
 * είναι όλα σταθερές ΚΑΙ αποδεικνύουν ότι θα τρέξει τουλάχιστον 1 φορά. Βλ.
 * `forToGuardedRepeatUntil` για τη μορφή που δουλεύει ΠΑΝΤΑ (με εξωτερικό ΑΝ),
 * όταν τα όρια είναι άγνωστες μεταβλητές (π.χ. διαβασμένη τιμή).
 */
export function forToRepeatUntil(stmt: ForStatement): readonly Statement[] | null {
  const step = stmt.step === null ? 1 : numericConstant(stmt.step);
  if (step === null || step === 0) return null;

  const fromNum = numericConstant(stmt.from);
  const toNum = numericConstant(stmt.to);
  if (fromNum === null || toNum === null) return null;
  const runsAtLeastOnce = step > 0 ? fromNum <= toNum : fromNum >= toNum;
  if (!runsAtLeastOnce) return null;

  return buildRepeatUntilFromFor(stmt, step);
}

/**
 * ΓΙΑ → Αν <από ΟΚ για ≥1 φορά> Τότε [ανάθεση, ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ] Τέλος_αν —
 * το "guarded" αντίστοιχο του `forToRepeatUntil`, ΠΑΝΤΑ δυνατό (σε αντίθεση
 * με το ΑΜΕΣΟ `forToRepeatUntil`, που απαιτεί ΣΤΑΘΕΡΑ όρια για να αποδείξει
 * ≥1 φορά ΕΚ ΤΩΝ ΠΡΟΤΕΡΩΝ): ο έλεγχος "θα έτρεχε έστω 1 φορά;" γίνεται ΣΤΟ
 * ΙΔΙΟ το πρόγραμμα, στο runtime — άρα δουλεύει ΑΚΟΜΑ ΚΙ όταν το `από`/`μέχρι`
 * είναι άγνωστες μεταβλητές (π.χ. διαβασμένη τιμή). Ίδια λογική με το
 * `whileToGuardedRepeatUntil`. Χρειάζεται μόνο το βήμα να είναι γνωστή
 * σταθερά (για να ξέρουμε τη φορά της σύγκρισης `<=`/`>=` στον φρουρό).
 */
export function forToGuardedRepeatUntil(stmt: ForStatement): readonly Statement[] | null {
  const step = stmt.step === null ? 1 : numericConstant(stmt.step);
  if (step === null || step === 0) return null;

  const [init, repeatStmt] = buildRepeatUntilFromFor(stmt, step);
  // Η ίδια η ΤΙΜΗ (δηλ. `stmt.variable`, ΟΧΙ `stmt.from`) — ο μετρητής έχει
  // ΗΔΗ πάρει αυτή την τιμή στο `init` παραπάνω, οπότε "χ <= λ" εδώ ΕΙΝΑΙ
  // ισοδύναμο με "από <= λ", απλά γραμμένο με το ίδιο όνομα που μόλις
  // αρχικοποιήθηκε αντί να επαναλαμβάνει την έκφραση `από`.
  const guardCondition: Expression = {
    kind: 'BinaryExpression',
    operator: step > 0 ? '<=' : '>=',
    left: stmt.variable,
    right: stmt.to,
    line: stmt.line,
    column: stmt.column,
  };

  // ΚΡΙΣΙΜΟ: το `init` (η αρχικοποίηση του μετρητή) μένει ΕΞΩ από το ΑΝ,
  // ΠΡΙΝ τον έλεγχο — ΑΚΡΙΒΩΣ όπως το πραγματικό ΓΙΑ αναθέτει ΠΑΝΤΑ την
  // αρχική τιμή στον μετρητή, ΑΣΧΕΤΑ αν ο βρόχος τρέξει έστω 1 φορά (βλ.
  // `executeFor` στον interpreter — "Σε βρόχο που ΔΕΝ έτρεξε καθόλου... ο
  // μετρητής μένει στην αρχική του τιμή"). Πριν αυτή τη διόρθωση το `init`
  // ζούσε ΜΕΣΑ στο `thenBranch` — αν ο φρουρός ήταν ψευδής (καμία επανάληψη),
  // ο μετρητής ΔΕΝ αρχικοποιούνταν καθόλου, και οποιαδήποτε εντολή ΜΕΤΑ το
  // ΑΝ (π.χ. ένα "Γράψε χ" μετά το πραγματικό ΓΙΑ, που δείχνει την τιμή που
  // ΑΠΕΤΥΧΕ τον τελικό έλεγχο) έσκαγε με "δεν έχει αρχικοποιηθεί" αντί να
  // δείξει τη σωστή, αρχική τιμή — bug report με παράδειγμα λ=0.
  const ifStmt: IfStatement = {
    kind: 'IfStatement',
    condition: guardCondition,
    thenBranch: [repeatStmt],
    elseIfBranches: [],
    elseBranch: null,
    endLine: null,
    line: stmt.line,
    column: stmt.column,
  };
  return [init, ifStmt];
}

export interface RepeatUntilToForMatch {
  readonly initAssignment: AssignmentStatement;
  readonly repeatStatement: RepeatUntilStatement;
  readonly apply: () => ForStatement;
}

/**
 * Το αντίστροφο του `forToRepeatUntil` — ίδια λογική με το `matchWhileAsFor`,
 * αλλά η συνθήκη εδώ είναι η ΣΤΑΣΗΣ, όχι η συνέχισης. Exported ΚΑΙ επειδή το
 * `array-fill-suggestion.ts` το χρησιμοποιεί (ίδιος λόγος με το
 * `matchWhileAsFor`).
 *
 * Δέχεται `>`/`<` ΑΜΕΣΑ (το "όριο" της ΜΕΧΡΙΣ_ΟΤΟΥ γίνεται απευθείας το
 * `ΜΕΧΡΙ` του ΓΙΑ — καμία ανοχή ανακρίβειας δεν χρειάζεται, αφού η ΓΙΑ
 * συνέχιση `<=`/`>=` ήδη καλύπτει κάθε ενδεχόμενη υπέρβαση). Δέχεται επίσης
 * `=`/`>=`/`<=`, αλλά ΜΟΝΟ όταν η αριθμητική ακολουθία (αρχική, αρχική+βήμα,
 * ...) "προσγειώνεται" ΑΚΡΙΒΩΣ πάνω στο όριο — decline don't guess, ίδιος
 * έλεγχος με το `matchIncrementFirstRepeatUntilAsFor` (χρησιμοποιεί το
 * ΠΡΑΓΜΑΤΙΚΟ βήμα του συγκεκριμένου βρόχου, όχι υποθετικό "+1" — δουλεύει
 * σωστά και για μη ακέραιο βήμα).
 */
export function matchRepeatUntilAsFor(initStmt: Statement, repeatStmt: Statement): RepeatUntilToForMatch | null {
  if (initStmt.kind !== 'AssignmentStatement' || initStmt.target.kind !== 'Identifier') return null;
  if (repeatStmt.kind !== 'RepeatUntilStatement') return null;
  const varName = initStmt.target.name;

  const condition = repeatStmt.condition;
  if (condition.kind !== 'BinaryExpression') return null;
  const op = condition.operator;
  if (op !== '>' && op !== '<' && op !== '=' && op !== '>=' && op !== '<=') return null;
  if (condition.left.kind !== 'Identifier' || condition.left.name !== varName) return null;

  if (repeatStmt.body.length === 0) return null;
  const lastIndex = repeatStmt.body.length - 1;
  const last = repeatStmt.body[lastIndex]!;
  if (last.kind !== 'AssignmentStatement' || last.target.kind !== 'Identifier' || last.target.name !== varName) return null;

  const delta = asLinearShift(last.value, varName);
  if (delta === null || delta === 0) return null;

  let to: Expression;
  if (op === '>' || op === '<') {
    if (op !== (delta > 0 ? '>' : '<')) return null;
    to = condition.right;
  } else {
    if (op === '>=' && delta < 0) return null;
    if (op === '<=' && delta > 0) return null;
    const initialNum = numericConstant(initStmt.value);
    const boundNum = numericConstant(condition.right);
    if (initialNum === null || boundNum === null) return null;
    if ((boundNum - initialNum) % delta !== 0) return null;
    const steps = (boundNum - initialNum) / delta;
    // steps < 1: η συνθήκη δεν ικανοποιείται ΠΟΤΕ μέσω κανονικής αύξησης
    // (π.χ. όριο = αρχική τιμή) — δεν αντιστοιχεί σε γνωστό, θετικό πλήθος
    // επαναλήψεων, άρα καμία μαντεψιά.
    if (steps < 1) return null;
    to = { kind: 'NumberLiteral', value: boundNum - delta, line: repeatStmt.line, column: repeatStmt.column };
  }

  const bodyWithoutIncrement = repeatStmt.body.slice(0, lastIndex);
  if (assignedNames(bodyWithoutIncrement).has(varName)) return null;

  const forStmt: ForStatement = {
    kind: 'ForStatement',
    variable: initStmt.target,
    from: initStmt.value,
    to,
    step: delta === 1 ? null : { kind: 'NumberLiteral', value: delta, line: repeatStmt.line, column: repeatStmt.column },
    body: bodyWithoutIncrement,
    // Συνθετικός κόμβος — βλ. σχόλιο `ForStatement.endLine`.
    endLine: null,
    line: initStmt.line,
    column: initStmt.column,
  };
  return { initAssignment: initStmt, repeatStatement: repeatStmt, apply: () => forStmt };
}

/**
 * Το ΑΛΛΟ κοινό σχολικό ιδίωμα "χειροκίνητου ΓΙΑ" με ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ: ο
 * μετρητής αυξάνεται ΠΡΩΤΟΣ μέσα στο σώμα (όχι τελευταίος, βλ.
 * `matchRepeatUntilAsFor` πιο πάνω) — π.χ. "Χ ← 18 / Αρχή_επανάληψης /
 * Χ ← Χ+2 / Γράψε Χ / Μέχρις_ότου Χ>=50" ≡ "Για Χ από 20 μέχρι 50 βήμα 2".
 * Δέχεται =, >, <, >=, <= στη ΜΕΧΡΙΣ_ΟΤΟΥ — αλλά ΜΟΝΟ όταν η αριθμητική
 * ακολουθία (αρχική+βήμα, αρχική+2×βήμα, ...) προσγειώνεται ΑΚΡΙΒΩΣ πάνω στο
 * γραμμένο όριο ("decline don't guess" — αλλιώς δεν είναι βέβαιο ποια είναι η
 * πρώτη τιμή που ικανοποιεί τη συνθήκη). Με >/< η τελευταία χρησιμοποιημένη
 * τιμή είναι ΕΝΑ βήμα πέρα από το γραμμένο όριο (ο έλεγχος γίνεται ΜΕΤΑ τη
 * χρήση, οπότε το όριο "ξεπερνιέται" πριν σταματήσει) — με >=/<=/= είναι
 * ΑΚΡΙΒΩΣ το γραμμένο όριο.
 */
export function matchIncrementFirstRepeatUntilAsFor(initStmt: Statement, repeatStmt: Statement): RepeatUntilToForMatch | null {
  if (initStmt.kind !== 'AssignmentStatement' || initStmt.target.kind !== 'Identifier') return null;
  if (repeatStmt.kind !== 'RepeatUntilStatement') return null;
  const varName = initStmt.target.name;

  const condition = repeatStmt.condition;
  if (condition.kind !== 'BinaryExpression') return null;
  const op = condition.operator;
  if (op !== '=' && op !== '>' && op !== '<' && op !== '>=' && op !== '<=') return null;
  if (condition.left.kind !== 'Identifier' || condition.left.name !== varName) return null;

  if (repeatStmt.body.length === 0) return null;
  const first = repeatStmt.body[0]!;
  if (first.kind !== 'AssignmentStatement' || first.target.kind !== 'Identifier' || first.target.name !== varName) return null;

  const delta = asLinearShift(first.value, varName);
  if (delta === null || delta === 0) return null;
  if ((op === '>' || op === '>=') && delta < 0) return null;
  if ((op === '<' || op === '<=') && delta > 0) return null;

  const initialNum = numericConstant(initStmt.value);
  const boundNum = numericConstant(condition.right);
  if (initialNum === null || boundNum === null) return null;
  if ((boundNum - initialNum) % delta !== 0) return null;

  const restOfBody = repeatStmt.body.slice(1);
  if (assignedNames(restOfBody).has(varName)) return null;

  const to = op === '>' || op === '<' ? boundNum + delta : boundNum;
  const forStmt: ForStatement = {
    kind: 'ForStatement',
    variable: initStmt.target,
    from: { kind: 'NumberLiteral', value: initialNum + delta, line: repeatStmt.line, column: repeatStmt.column },
    to: { kind: 'NumberLiteral', value: to, line: repeatStmt.line, column: repeatStmt.column },
    step: delta === 1 ? null : { kind: 'NumberLiteral', value: delta, line: repeatStmt.line, column: repeatStmt.column },
    body: restOfBody,
    // Συνθετικός κόμβος — βλ. σχόλιο `ForStatement.endLine`.
    endLine: null,
    line: initStmt.line,
    column: initStmt.column,
  };
  return { initAssignment: initStmt, repeatStatement: repeatStmt, apply: () => forStmt };
}

/** Σαρώνει μια λίστα εντολών για γειτονικά ζεύγη [ανάθεση αρχικοποίησης, ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ] που ταιριάζουν σε ΟΠΟΙΟΔΗΠΟΤΕ από τα δύο μοτίβα μετρητή (αύξηση τελευταία: `matchRepeatUntilAsFor`, ή πρώτη: `matchIncrementFirstRepeatUntilAsFor`). */
export function findForLoopsFromRepeatUntil(statements: readonly Statement[]): RepeatUntilToForMatch[] {
  const matches: RepeatUntilToForMatch[] = [];
  for (let i = 0; i + 1 < statements.length; i++) {
    const match = matchRepeatUntilAsFor(statements[i]!, statements[i + 1]!) ?? matchIncrementFirstRepeatUntilAsFor(statements[i]!, statements[i + 1]!);
    if (match !== null) matches.push(match);
  }
  return matches;
}

// --- ΟΣΟ ↔ ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ (με εξωτερικό έλεγχο ΑΝ) ---------------------------

/**
 * ΟΣΟ → Αν <συνθήκη> Τότε [ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ ... ΜΕΧΡΙΣ_ΟΤΟΥ ¬<συνθήκη>]
 * Τέλος_αν. Το ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ τρέχει ΠΑΝΤΑ ≥1 φορά, ενώ το ΟΣΟ μπορεί να
 * μην τρέξει καθόλου — ο εξωτερικός έλεγχος ΑΝ είναι απαραίτητος για να
 * παραμείνει σωστό όταν η συνθήκη είναι ψευδής εξαρχής. Πάντα δυνατή (καμία
 * προϋπόθεση) — η άρνηση γίνεται χωρίς ΟΧΙ όποτε είναι αλγεβρικά δυνατό
 * (βλ. `negateCondition`).
 */
/**
 * ΟΣΟ → ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ ... ΜΕΧΡΙΣ_ΟΤΟΥ ¬<συνθήκη> — ΧΩΡΙΣ εξωτερικό ΑΝ-φρουρό
 * (το "άμεσο" αντίστοιχο του `whileToGuardedRepeatUntil`, ίδια λογική με το
 * `forToRepeatUntil` vs `forToGuardedRepeatUntil`). Προσφέρεται ΜΟΝΟ όταν
 * μπορούμε να ΑΠΟΔΕΙΞΟΥΜΕ — από ένα πρόθεμα απλών αναθέσεων σταθεράς αμέσως
 * πριν τον βρόχο (βλ. `collectConstantPrefix`) — ότι η συνθήκη ΟΣΟ είναι ΗΔΗ
 * αληθής πριν την πρώτη επανάληψη· τότε το ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ (που τρέχει ΠΑΝΤΑ
 * ≥1 φορά) δεν παρεκκλίνει ΠΟΤΕ από το ΟΣΟ, άρα ο φρουρός είναι περιττός. Αν
 * δεν αποδεικνύεται (π.χ. η συνθήκη εξαρτάται από ΔΙΑΒΑΣΕ/πίνακα/παράμετρο,
 * ή αποδεικνύεται ότι είναι ψευδής), `null` — καμία μαντεψιά, ο καλών πέφτει
 * πίσω στο πάντα-ασφαλές `whileToGuardedRepeatUntil`.
 */
export function whileToRepeatUntil(before: readonly Statement[], stmt: WhileStatement): RepeatUntilStatement | null {
  const env = collectConstantPrefix(before);
  const runsAtLeastOnce = evalConstant(stmt.condition, env);
  if (typeof runsAtLeastOnce !== 'boolean' || !runsAtLeastOnce) return null;

  const stopCondition = negateCondition(stmt.condition, stmt);
  return { kind: 'RepeatUntilStatement', body: stmt.body, condition: stopCondition, line: stmt.line, column: stmt.column };
}

export function whileToGuardedRepeatUntil(stmt: WhileStatement): IfStatement {
  const stopCondition = negateCondition(stmt.condition, stmt);
  const repeatStmt: RepeatUntilStatement = { kind: 'RepeatUntilStatement', body: stmt.body, condition: stopCondition, line: stmt.line, column: stmt.column };
  return {
    kind: 'IfStatement',
    condition: stmt.condition,
    thenBranch: [repeatStmt],
    elseIfBranches: [],
    elseBranch: null,
    endLine: null,
    line: stmt.line,
    column: stmt.column,
  };
}

/**
 * Αναγνωρίζει ΑΚΡΙΒΩΣ το σχήμα που παράγει το `whileToGuardedRepeatUntil`:
 * μια Απλή ΑΝ (χωρίς ΑΛΛΙΩΣ/ΑΛΛΙΩΣ_ΑΝ) με ΤΟΤΕ ΑΚΡΙΒΩΣ ένα ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ,
 * του οποίου η συνθήκη ΜΕΧΡΙΣ_ΟΤΟΥ είναι η άρνηση της συνθήκης του ΑΝ (ρητή
 * ΟΧΙ, αντίστροφος συγκριτικός τελεστής, ή De Morgan — ό,τι αναγνωρίζει το
 * `isDirectNegation` στο selection.ts). Επιστρέφει `null` αν δεν ταιριάζει
 * ακριβώς — ΔΕΝ μαντεύει.
 */
export function matchGuardedRepeatUntilAsWhile(ifStmt: IfStatement): WhileStatement | null {
  if (ifStmt.elseIfBranches.length > 0 || ifStmt.elseBranch !== null) return null;
  if (ifStmt.thenBranch.length !== 1) return null;
  const inner = ifStmt.thenBranch[0]!;
  if (inner.kind !== 'RepeatUntilStatement') return null;
  if (!isDirectNegation(ifStmt.condition, inner.condition)) return null;
  // Συνθετικός κόμβος — βλ. σχόλιο `WhileStatement.endLine`.
  return { kind: 'WhileStatement', condition: ifStmt.condition, body: inner.body, endLine: null, line: ifStmt.line, column: ifStmt.column };
}

// --- «Γυμνό» ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ → ΟΣΟ (χωρίς φρουρό/διπλασιασμό, με απόδειξη) ----

/**
 * Πολύ περιορισμένος, ΑΣΦΑΛΗΣ constant-folding υπολογιστής εκφράσεων — μόνο
 * αριθμοί/λογικές τιμές/ταυτότητες με ΓΝΩΣΤΗ (σταθερή) τιμή στο `env`,
 * βασικοί τελεστές. `null` όποτε ΔΕΝ μπορεί να αποδείξει ΠΛΗΡΩΣ μια τιμή
 * (άγνωστη μεταβλητή, ΔΙΑΒΑΣΕ/κλήση συνάρτησης/πίνακας, διαίρεση με 0) — ΠΟΤΕ
 * δεν μαντεύει/προσεγγίζει.
 */
function evalConstant(expr: Expression, env: ReadonlyMap<string, number | boolean>): number | boolean | null {
  switch (expr.kind) {
    case 'NumberLiteral':
      return expr.value;
    case 'BooleanLiteral':
      return expr.value;
    case 'Identifier': {
      const value = env.get(expr.name);
      return value === undefined ? null : value;
    }
    case 'UnaryExpression': {
      const operand = evalConstant(expr.operand, env);
      if (operand === null) return null;
      if (expr.operator === '-') return typeof operand === 'number' ? -operand : null;
      if (expr.operator === 'ΟΧΙ') return typeof operand === 'boolean' ? !operand : null;
      return null;
    }
    case 'BinaryExpression': {
      const left = evalConstant(expr.left, env);
      if (left === null) return null;
      const right = evalConstant(expr.right, env);
      if (right === null) return null;
      if (typeof left === 'number' && typeof right === 'number') {
        switch (expr.operator) {
          case '+':
            return left + right;
          case '-':
            return left - right;
          case '*':
            return left * right;
          case '/':
            return right === 0 ? null : left / right;
          case 'DIV':
            return right === 0 ? null : Math.trunc(left / right);
          case 'MOD':
            return right === 0 ? null : left % right;
          case '^':
            return left ** right;
          case '=':
            return left === right;
          case '<>':
            return left !== right;
          case '<':
            return left < right;
          case '>':
            return left > right;
          case '<=':
            return left <= right;
          case '>=':
            return left >= right;
          default:
            return null;
        }
      }
      if (typeof left === 'boolean' && typeof right === 'boolean') {
        if (expr.operator === 'ΚΑΙ') return left && right;
        if (expr.operator === 'Η') return left || right;
      }
      return null;
    }
    default:
      // StringLiteral/ArrayAccess/FunctionCall — χρειάζονται runtime context, δεν αποδεικνύονται στατικά εδώ.
      return null;
  }
}

/**
 * Σαρώνει ΠΡΟΣ ΤΑ ΠΙΣΩ ένα "πρόθεμα" απλών αναθέσεων σταθεράς σε απλή
 * μεταβλητή (`όνομα ← <σταθεροποιήσιμη έκφραση>`) ΑΜΕΣΩΣ πριν από μια θέση —
 * σταματάει στην πρώτη εντολή που ΔΕΝ ταιριάζει (π.χ. ΔΙΑΒΑΣΕ, ΑΝ, ανάθεση σε
 * πίνακα, ή ανάθεση τιμής που εξαρτάται από άγνωστη μεταβλητή). Η ΠΡΩΤΗ φορά
 * που συναντάται (ανάστροφα) ένα όνομα είναι η ΠΙΟ ΠΡΟΣΦΑΤΗ ανάθεσή του, άρα
 * αγνοούνται τυχόν παλιότερες.
 */
function collectConstantPrefix(before: readonly Statement[]): Map<string, number | boolean> {
  const env = new Map<string, number | boolean>();
  for (let i = before.length - 1; i >= 0; i--) {
    const stmt = before[i]!;
    if (stmt.kind !== 'AssignmentStatement' || stmt.target.kind !== 'Identifier') break;
    const value = evalConstant(stmt.value, env);
    if (value === null) break;
    if (!env.has(stmt.target.name)) env.set(stmt.target.name, value);
  }
  return env;
}

/**
 * «Γυμνό» ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ → ΟΣΟ — ΧΩΡΙΣ εξωτερικό ΑΝ-φρουρό και ΧΩΡΙΣ
 * διπλασιασμό του σώματος (βλ. `whileToGuardedRepeatUntil` για τη γενική
 * περίπτωση, που ΠΑΝΤΑ χρειάζεται φρουρό). Προσφέρεται ΜΟΝΟ όταν μπορούμε να
 * ΑΠΟΔΕΙΞΟΥΜΕ — από ένα πρόθεμα απλών αναθέσεων σταθεράς αμέσως πριν τον
 * βρόχο (βλ. `collectConstantPrefix`) — ότι η άρνηση της συνθήκης
 * ΜΕΧΡΙΣ_ΟΤΟΥ ισχύει ΗΔΗ πριν από την πρώτη επανάληψη· τότε το ΟΣΟ δεν θα
 * παρέλειπε ΠΟΤΕ το σώμα, άρα η μετατροπή είναι ακριβώς ισοδύναμη. Αν δεν
 * αποδεικνύεται (π.χ. η συνθήκη εξαρτάται από ΔΙΑΒΑΣΕ/πίνακα/παράμετρο, ή
 * αποδεικνύεται ότι ΘΑ σταματούσε αμέσως), `null` — καμία μαντεψιά.
 */
export function matchRepeatUntilAsWhile(before: readonly Statement[], repeatStmt: RepeatUntilStatement): WhileStatement | null {
  const env = collectConstantPrefix(before);
  const stopsImmediately = evalConstant(repeatStmt.condition, env);
  if (typeof stopsImmediately !== 'boolean' || stopsImmediately) return null;

  const continueCondition = negateCondition(repeatStmt.condition, repeatStmt);
  // Συνθετικός κόμβος — βλ. σχόλιο `WhileStatement.endLine`.
  return { kind: 'WhileStatement', condition: continueCondition, body: repeatStmt.body, endLine: null, line: repeatStmt.line, column: repeatStmt.column };
}

/**
 * ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ → [σώμα (1η εκτέλεση, εκτός βρόχου), ΟΣΟ ¬<συνθήκη> ΕΠΑΝΑΛΑΒΕ
 * [το ΙΔΙΟ σώμα]] — ΠΑΝΤΑ έγκυρη μετατροπή, ΧΩΡΙΣ να χρειάζεται απόδειξη
 * τίποτα (σε αντίθεση με το `matchRepeatUntilAsWhile`, που αρνείται όταν η
 * συνθήκη εξαρτάται από κάτι άγνωστο, π.χ. μεταβλητή που διαβάζεται ΜΕΣΑ στο
 * ίδιο το σώμα): η 1η εκτέλεση του σώματος γίνεται ΑΝΕΥ ΟΡΩΝ — όπως ΑΚΡΙΒΩΣ
 * θα γινόταν στο αρχικό ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ — άρα ΔΕΝ χρειάζεται να ξέρουμε την
 * τιμή της συνθήκης πριν από αυτήν· το ΟΣΟ μετά απλώς ελέγχει πριν από ΚΑΘΕ
 * ΕΠΟΜΕΝΗ επανάληψη, οπότε οποιαδήποτε μεταβλητή που «γεννιέται» μέσα στο
 * σώμα (π.χ. από ΔΙΑΒΑΣΕ) έχει ήδη τιμή.
 *
 * Το αντίτιμο είναι ο διπλασιασμός του σώματος στο τυπωμένο αποτέλεσμα — γι'
 * αυτό προσφέρεται ΜΟΝΟ ως fallback, όταν το `matchRepeatUntilAsWhile` (που
 * παράγει ΚΑΘΑΡΟ, χωρίς διπλασιασμό ΟΣΟ, αλλά μόνο αν αποδεικνύεται ασφαλές)
 * αρνείται — βλ. `detectRepetitionStructures`. Ο κόμβος του σώματος
 * ΕΠΑΝΑΧΡΗΣΙΜΟΠΟΙΕΙΤΑΙ (όχι βαθύ clone) σε αμφότερες τις θέσεις — ασφαλές,
 * μόνο για τύπωμα/αντικατάσταση σε στατικό δέντρο, ίδια σύμβαση με το
 * `[...stmt.body, increment]` στο `buildRepeatUntilFromFor` παραπάνω.
 */
export function repeatUntilToWhile(repeatStmt: RepeatUntilStatement): readonly Statement[] {
  const continueCondition = negateCondition(repeatStmt.condition, repeatStmt);
  const whileStmt: WhileStatement = {
    kind: 'WhileStatement',
    condition: continueCondition,
    body: repeatStmt.body,
    endLine: null,
    line: repeatStmt.line,
    column: repeatStmt.column,
  };
  return [...repeatStmt.body, whileStmt];
}
